import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, RefreshCw, Search, CheckCircle2, XCircle, Clock, Building2, Link as LinkIcon, Undo2, AlertCircle, Ban } from 'lucide-react';
import { useEmailDomainObservations } from '../hooks/useEmailDomainObservations';
import { useAuth } from '../hooks/useAuth';
import { useContacts } from '../hooks/useContacts';
import { colaboradores } from '../utils/stageHelpers';
import { supabase } from '../supabaseClient';
import useBulkSelection from '../components/shared/useBulkSelection';
import BulkActionBar from '../components/shared/BulkActionBar';
import SelectAllCheckbox from '../components/shared/SelectAllCheckbox';

const BULK_CHUNK = 50;

const STATUS_BADGES = {
  tracked: { label: 'tracked', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  not_applicable: { label: 'not applicable', color: 'bg-slate-200 text-slate-700', icon: XCircle },
  pending: { label: 'pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
};

const NA_REASONS = [
  'Fornecedor interno',
  'Email pessoal fora de contexto',
  'Serviço automático (SaaS, newsletters)',
  'Cliente/lead fora de escopo',
  'Outro',
];

const TIPO_EMPRESA_OPCOES = [
  { value: 'developer',       label: 'Incorporadora / Construtora (developer)' },
  { value: 'end_user',        label: 'Comprador institucional (end_user)' },
  { value: 'investment_fund', label: 'Fundo de investimento (investment_fund)' },
  { value: 'law_firm',        label: 'Escritório de advocacia (law_firm)' },
  { value: 'consulting',      label: 'Consultoria (consulting)' },
  { value: 'landowner',       label: 'Proprietário de terreno (landowner)' },
  { value: 'real_estate_agency', label: 'Imobiliária (real_estate_agency)' },
  { value: 'real_estate_broker', label: 'Corretora de imóveis (real_estate_broker)' },
  { value: 'builder',         label: 'Construtora de execução (builder)' },
  { value: 'notary',          label: 'Cartório (notary)' },
  { value: 'tool_saas',       label: 'Ferramenta / SaaS (tool_saas)' },
];

function timeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return `há ${diffSec}s`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d}d`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `há ${mo}mes`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

const Badge = ({ status, reason }) => {
  const conf = STATUS_BADGES[status] || STATUS_BADGES.pending;
  const Icon = conf.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${conf.color}`}
      title={reason || conf.label}
    >
      <Icon size={11} />
      {conf.label}
    </span>
  );
};

const NotApplicableModal = ({ domain, onClose, onConfirm }) => {
  const [reason, setReason] = useState(NA_REASONS[2]);
  const [detail, setDetail] = useState('');
  const [saving, setSaving] = useState(false);
  const fullReason = reason === 'Outro' && detail.trim() ? `Outro: ${detail.trim()}` : (reason === 'Outro' ? 'Outro' : reason);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-md mx-4">
        <h3 className="text-base font-semibold text-slate-800 mb-3">Marcar {domain} como not applicable</h3>
        <label className="block text-xs text-slate-600 mb-1">Motivo</label>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm"
        >
          {NA_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        {reason === 'Outro' && (
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Detalhe..."
            rows={2}
            className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm"
          />
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
          <button
            onClick={async () => { setSaving(true); await onConfirm(fullReason); setSaving(false); }}
            disabled={saving}
            className="px-3 py-1.5 text-sm bg-slate-700 text-white rounded hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateEmpresaFromDomainModal = ({ domain, onClose, onConfirm }) => {
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState(`https://${domain}`);
  const [tipoEmpresa, setTipoEmpresa] = useState('developer');
  const [saving, setSaving] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-md mx-4">
        <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Building2 size={18} className="text-blue-600" /> Criar empresa a partir de {domain}
        </h3>
        <label className="block text-xs text-slate-600 mb-1">Nome da empresa (gravado em MAIÚSCULO)</label>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="ex: Pátria Investimentos"
          autoFocus
          className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm"
        />
        <label className="block text-xs text-slate-600 mb-1">Website</label>
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm"
        />
        <label className="block text-xs text-slate-600 mb-1">Tipo de empresa <span className="text-red-600">*</span></label>
        <select
          value={tipoEmpresa}
          onChange={(e) => setTipoEmpresa(e.target.value)}
          className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm bg-white"
        >
          {TIPO_EMPRESA_OPCOES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
          <button
            onClick={async () => {
              setSaving(true);
              await onConfirm({ company_name: companyName.trim(), website: website.trim(), tipo_empresa: tipoEmpresa });
              setSaving(false);
            }}
            disabled={!companyName.trim() || !tipoEmpresa || saving}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Criando...' : 'Criar empresa + link'}
          </button>
        </div>
      </div>
    </div>
  );
};

const LinkExistingModal = ({ domain, onClose, onConfirm, empresasList, fetchTipoDefault }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [tipoEmpresa, setTipoEmpresa] = useState('developer');
  const [saving, setSaving] = useState(false);
  const [tipoLoading, setTipoLoading] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return empresasList.slice(0, 10);
    return empresasList.filter((e) =>
      (e.empresa || '').toLowerCase().includes(q) ||
      (e.company_name || '').toLowerCase().includes(q) ||
      (e.website || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [query, empresasList]);

  useEffect(() => {
    if (!selected || !fetchTipoDefault) return;
    let cancelled = false;
    setTipoLoading(true);
    fetchTipoDefault(selected.id).then((tipo) => {
      if (!cancelled) { setTipoEmpresa(tipo || 'developer'); setTipoLoading(false); }
    }).catch(() => { if (!cancelled) setTipoLoading(false); });
    return () => { cancelled = true; };
  }, [selected, fetchTipoDefault]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-md mx-4">
        <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <LinkIcon size={18} className="text-indigo-600" /> Ligar {domain} a empresa existente
        </h3>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome ou website..."
          autoFocus
          className="w-full mb-2 px-3 py-2 border border-slate-300 rounded text-sm"
        />
        <div className="max-h-60 overflow-y-auto border border-slate-200 rounded mb-3">
          {filtered.length === 0 && <div className="p-3 text-xs text-slate-500">Nenhum resultado</div>}
          {filtered.map((e) => (
            <div
              key={e.id}
              onClick={() => setSelected(e)}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${selected?.id === e.id ? 'bg-indigo-50' : ''}`}
            >
              <div className="font-medium text-slate-800">{e.empresa || e.company_name}</div>
              {e.website && <div className="text-[10px] text-slate-500">{e.website}</div>}
            </div>
          ))}
        </div>
        {selected && (
          <>
            <label className="block text-xs text-slate-600 mb-1">
              Tipo de empresa <span className="text-red-600">*</span>
              {tipoLoading && <span className="ml-1 text-slate-400">carregando...</span>}
            </label>
            <select
              value={tipoEmpresa}
              onChange={(e) => setTipoEmpresa(e.target.value)}
              disabled={tipoLoading}
              className="w-full mb-3 px-3 py-2 border border-slate-300 rounded text-sm bg-white"
            >
              {TIPO_EMPRESA_OPCOES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
          <button
            onClick={async () => {
              if (!selected) return;
              setSaving(true);
              await onConfirm(selected, tipoEmpresa);
              setSaving(false);
            }}
            disabled={!selected || !tipoEmpresa || saving || tipoLoading}
            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Ligando...' : 'Ligar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function EmailCoveragePage() {
  const { rows, loading, error, auditRunning, lastRunSummary, refetch, runAudit, markNotApplicable, linkToExistingEmpresa, createEmpresaFromDomain, undoLink, reevaluate, fetchTipoEmpresaDefault } = useEmailDomainObservations();
  const { user } = useAuth();
  const { contatos } = useContacts();

  const [statusFilter, setStatusFilter] = useState('pending');
  const [minLast30, setMinLast30] = useState(0);
  const [searchDomain, setSearchDomain] = useState('');
  const [sortBy, setSortBy] = useState('last_30d_count');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedColabs, setSelectedColabs] = useState(() => colaboradores.filter(c => c.id !== 'all').map(c => c.id));
  const [daysBack, setDaysBack] = useState(365);

  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState(null);
  const sel = useBulkSelection();

  const showToast = (message, tone = 'success') => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2500);
  };

  // Bulk NA: pede UMA razão obrigatória + UPDATE chunked-50 com .in('domain', ...).
  // Em erro: toast + refetch + throw (mantém seleção na BulkActionBar).
  const bulkMarkNA = async () => {
    const domains = sel.getSelectedIds();
    if (domains.length === 0) return;

    const reason = window.prompt(
      'Motivo para marcar como N/A (ex: provedor genérico, notificação automática):',
      '',
    );
    if (reason === null || !reason.trim()) {
      showToast('Razão obrigatória para marcar domínios como N/A em lote.', 'error');
      throw new Error('reason required');
    }

    const updatedAt = new Date().toISOString();
    const updatedBy = user?.email || 'desconhecido';

    for (let i = 0; i < domains.length; i += BULK_CHUNK) {
      const chunk = domains.slice(i, i + BULK_CHUNK);
      const { error: updErr } = await supabase
        .from('email_domain_observations')
        .update({
          tracking_status: 'not_applicable',
          not_applicable_reason: reason.trim(),
          updated_at: updatedAt,
          updated_by: updatedBy,
        })
        .in('domain', chunk);
      if (updErr) {
        showToast(`Erro ao marcar N/A (lote): ${updErr.message}`, 'error');
        await refetch();
        throw updErr;
      }
    }

    await refetch();
    showToast(
      `${domains.length} ${domains.length === 1 ? 'domínio marcado' : 'domínios marcados'} como N/A`,
      'success',
    );
  };

  const empresasList = useMemo(() => {
    const seen = new Map();
    (contatos || []).forEach((c) => {
      const e = c.empresas;
      if (e?.id && !seen.has(e.id)) seen.set(e.id, e);
    });
    return [...seen.values()].sort((a, b) => (a.empresa || '').localeCompare(b.empresa || ''));
  }, [contatos]);

  const counts = useMemo(() => {
    const out = { tracked: 0, not_applicable: 0, pending: 0 };
    (rows || []).forEach((r) => {
      if (r.tracking_status in out) out[r.tracking_status]++;
    });
    return out;
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows || [];
    if (statusFilter !== 'all') list = list.filter((r) => r.tracking_status === statusFilter);
    if (minLast30 > 0) list = list.filter((r) => r.last_30d_count >= minLast30);
    if (searchDomain.trim()) {
      const q = searchDomain.trim().toLowerCase();
      list = list.filter((r) => (r.domain || '').toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, statusFilter, minLast30, searchDomain, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir(col === 'domain' ? 'asc' : 'desc'); }
  };

  const handleRun = useCallback(async () => {
    const res = await runAudit({ colaborador_ids: selectedColabs, days_back: Number(daysBack) });
    if (!res.success) alert(`Erro na auditoria: ${res.error}`);
  }, [runAudit, selectedColabs, daysBack]);

  const handleMarkNA = (domain, reason) => markNotApplicable(domain, reason, user?.email);
  const handleLink = (domain, emp, tipoEmpresa) => linkToExistingEmpresa(domain, emp.id, user?.email, tipoEmpresa);
  const handleCreate = (domain, payload) => createEmpresaFromDomain(domain, payload, user?.email);
  const handleUndo = (domain) => {
    if (!window.confirm('Desfazer link e voltar para pending?')) return;
    return undoLink(domain, user?.email);
  };
  const handleReeval = (domain) => {
    if (!window.confirm('Reavaliar e voltar para pending?')) return;
    return reevaluate(domain, user?.email);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
        <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <div className="border-l border-slate-200 pl-4">
          <h1 className="text-base font-semibold text-slate-800">Cobertura de domínios de email</h1>
          <p className="text-[11px] text-slate-500">Domínios observados em emails dos colaboradores vs empresas cadastradas</p>
        </div>
      </header>

      <main className="p-6 max-w-screen-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5">
              <CheckCircle2 size={14} className="text-emerald-600" />
              <span className="text-sm text-emerald-700">Tracked: <strong>{counts.tracked}</strong></span>
            </div>
            <div className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-md px-3 py-1.5">
              <XCircle size={14} className="text-slate-500" />
              <span className="text-sm text-slate-700">Not applicable: <strong>{counts.not_applicable}</strong></span>
            </div>
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
              <Clock size={14} className="text-amber-600" />
              <span className="text-sm text-amber-700">Pending: <strong>{counts.pending}</strong></span>
            </div>
            {lastRunSummary && (
              <span className="text-xs text-slate-500 ml-auto">
                Último run: +{lastRunSummary.new_domains_this_run} novos · {lastRunSummary.auto_linked_this_run} auto-linkados
              </span>
            )}
          </div>

          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-600">Colaboradores:</label>
              <div className="flex gap-1">
                {colaboradores.filter(c => c.id !== 'all').map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedColabs((prev) => prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id])}
                    className={`px-2 py-0.5 text-[11px] rounded border ${selectedColabs.includes(c.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}
                  >
                    {c.nome.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-600">days_back:</label>
              <input
                type="number"
                value={daysBack}
                onChange={(e) => setDaysBack(e.target.value)}
                min={30}
                max={730}
                className="w-20 px-2 py-1 border border-slate-300 rounded text-sm"
              />
            </div>
            <button
              onClick={handleRun}
              disabled={auditRunning || selectedColabs.length === 0}
              className="ml-auto px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Play size={14} /> {auditRunning ? 'Rodando... (pode demorar minutos)' : 'Rodar auditoria'}
            </button>
            <button onClick={refetch} className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1">
              <RefreshCw size={14} /> Atualizar lista
            </button>
          </div>

          <div className="px-5 py-2 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <div className="flex gap-1">
              {['pending', 'tracked', 'not_applicable', 'all'].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 text-xs rounded ${statusFilter === s ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {s === 'all' ? 'Todos' : (STATUS_BADGES[s]?.label || s)}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-600">min 30d:</label>
              <input
                type="number"
                value={minLast30}
                onChange={(e) => setMinLast30(Number(e.target.value) || 0)}
                min={0}
                className="w-16 px-2 py-1 border border-slate-300 rounded text-xs"
              />
            </div>
            <div className="flex items-center gap-1 ml-auto">
              <Search size={14} className="text-slate-400" />
              <input
                type="text"
                value={searchDomain}
                onChange={(e) => setSearchDomain(e.target.value)}
                placeholder="Buscar domínio..."
                className="px-2 py-1 border border-slate-300 rounded text-xs w-48"
              />
            </div>
          </div>

          <div className="overflow-auto">
            {loading && <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>}
            {error && !loading && (
              <div className="m-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                <AlertCircle size={14} /> {error}
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">Nenhum domínio corresponde aos filtros.</div>
            )}
            {!loading && !error && filtered.length > 0 && (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left whitespace-nowrap">
                      <SelectAllCheckbox
                        totalCount={filtered.length}
                        selectedCount={sel.count}
                        onSelectAll={() => sel.selectAll(filtered.map((r) => r.domain))}
                        onDeselectAll={sel.deselectAll}
                      />
                    </th>
                    <th onClick={() => toggleSort('domain')} className="px-3 py-2 text-left cursor-pointer">Domínio</th>
                    <th onClick={() => toggleSort('last_30d_count')} className="px-3 py-2 text-right cursor-pointer">30d</th>
                    <th onClick={() => toggleSort('last_90d_count')} className="px-3 py-2 text-right cursor-pointer">90d</th>
                    <th onClick={() => toggleSort('total_email_count')} className="px-3 py-2 text-right cursor-pointer">Total</th>
                    <th onClick={() => toggleSort('last_seen_at')} className="px-3 py-2 text-left cursor-pointer">Último</th>
                    <th className="px-3 py-2 text-left">Top subject</th>
                    <th className="px-3 py-2 text-left">Top sender</th>
                    <th onClick={() => toggleSort('tracking_status')} className="px-3 py-2 text-left cursor-pointer">Status</th>
                    <th className="px-3 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const topSubject = (r.sample_subjects && r.sample_subjects[0]) || '—';
                    const topSender = (r.sample_senders && r.sample_senders[0]) || '—';
                    const isSelected = sel.isSelected(r.domain);
                    return (
                      <tr
                        key={r.domain}
                        className={`border-b border-slate-100 ${
                          isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => sel.toggle(r.domain)}
                            aria-label={`Selecionar ${r.domain}`}
                            className="cursor-pointer w-4 h-4 accent-slate-700 rounded"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-800">{r.domain}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.last_30d_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.last_90d_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.total_email_count}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{timeAgo(r.last_seen_at)}</td>
                        <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[220px]" title={topSubject}>{topSubject}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[160px]" title={topSender}>{topSender}</td>
                        <td className="px-3 py-2">
                          {r.tracking_status === 'tracked' && r.empresas ? (
                            <span title={`Ligado a ${r.empresas.empresa || r.empresas.company_name}`}>
                              <Badge status="tracked" /> <span className="text-[10px] text-slate-500 ml-1">{r.empresas.empresa || r.empresas.company_name}</span>
                            </span>
                          ) : (
                            <Badge status={r.tracking_status} reason={r.not_applicable_reason} />
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.tracking_status === 'pending' && (
                            <div className="inline-flex gap-1">
                              <button
                                onClick={() => setConfirmModal({ kind: 'create', domain: r.domain })}
                                className="px-2 py-1 text-[11px] bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                                title="Criar empresa a partir deste domínio"
                              >Criar empresa</button>
                              <button
                                onClick={() => setConfirmModal({ kind: 'link', domain: r.domain })}
                                className="px-2 py-1 text-[11px] bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                                title="Ligar a empresa existente"
                              >Ligar</button>
                              <button
                                onClick={() => setConfirmModal({ kind: 'na', domain: r.domain })}
                                className="px-2 py-1 text-[11px] bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                                title="Marcar not applicable"
                              >NA</button>
                            </div>
                          )}
                          {r.tracking_status === 'tracked' && (
                            <button
                              onClick={() => handleUndo(r.domain)}
                              className="px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded inline-flex items-center gap-1"
                              title="Desfazer link"
                            ><Undo2 size={11} /> Desfazer</button>
                          )}
                          {r.tracking_status === 'not_applicable' && (
                            <button
                              onClick={() => handleReeval(r.domain)}
                              className="px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded"
                              title="Reavaliar"
                            >Reavaliar</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {confirmModal?.kind === 'na' && (
        <NotApplicableModal
          domain={confirmModal.domain}
          onClose={() => setConfirmModal(null)}
          onConfirm={async (reason) => {
            await handleMarkNA(confirmModal.domain, reason);
            setConfirmModal(null);
          }}
        />
      )}
      {confirmModal?.kind === 'create' && (
        <CreateEmpresaFromDomainModal
          domain={confirmModal.domain}
          onClose={() => setConfirmModal(null)}
          onConfirm={async (payload) => {
            const res = await handleCreate(confirmModal.domain, payload);
            if (!res?.success) alert(`Erro: ${res?.error || 'desconhecido'}`);
            setConfirmModal(null);
          }}
        />
      )}
      {confirmModal?.kind === 'link' && (
        <LinkExistingModal
          domain={confirmModal.domain}
          empresasList={empresasList}
          fetchTipoDefault={fetchTipoEmpresaDefault}
          onClose={() => setConfirmModal(null)}
          onConfirm={async (emp, tipoEmpresa) => {
            await handleLink(confirmModal.domain, emp, tipoEmpresa);
            setConfirmModal(null);
          }}
        />
      )}

      <BulkActionBar
        count={sel.count}
        onClear={sel.deselectAll}
        actions={[
          {
            label: 'Marcar NA selecionados',
            icon: Ban,
            color: 'gray',
            confirmMessage: (n) => `Marcar ${n} domínio${n === 1 ? '' : 's'} como N/A?`,
            onClick: bulkMarkNA,
          },
        ]}
      />

      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 px-3 py-2 rounded shadow-lg text-xs font-medium ${
            toast.tone === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-emerald-600 text-white'
          }`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
